import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LuListX, LuShuffle } from 'react-icons/lu';
import { ProblemTable } from './ProblemTable';
import { TodayHero } from './TodayHero';
import { ProgressPanel } from './ProgressPanel';
import { StreakCalendar } from './StreakCalendar';
import { ContestPanel } from './ContestPanel';
import { DIFFICULTIES, applyFilters, hasActiveFilters, readFilters, writeFilters } from './problemFilters';
import { LAST_PROBLEM_KEY } from './ProblemPage';
import { Button } from '../../ui/Button';
import { SearchInput, Select } from '../../ui/Form';
import { Chip, SegmentedControl } from '../../ui/Navigation';
import { Alert, EmptyState, Skeleton } from '../../ui/Feedback';
import { Kbd } from '../../ui/Labels';
import { useToast } from '../../ui/Toast';
import { useDocumentTitle, useStrings } from '../../tenant/TenantProvider';
import { useCatalog, featuredContest } from '../../stores/catalog';
import { practiceApi } from '../../lib/practiceApi';
import { storage } from '../../lib/storage';

const TOPIC_PREVIEW = 14;

function TableSkeleton() {
  return (
    <div className="problem-skeleton" aria-hidden="true">
      {Array.from({ length: 8 }, (_, index) => (
        <div key={index} className="problem-skeleton__row">
          <Skeleton width={16} height={16} />
          <Skeleton width={24} />
          <Skeleton width={`${40 + ((index * 17) % 35)}%`} />
          <Skeleton width={40} style={{ marginLeft: 'auto' }} />
          <Skeleton width={56} />
        </div>
      ))}
    </div>
  );
}

function TopicChips({ topics, active, onToggle }) {
  const t = useStrings();
  const [expanded, setExpanded] = useState(false);
  const activeIndex = topics.findIndex((topic) => topic.topic === active);
  const visible = expanded || topics.length <= TOPIC_PREVIEW ? topics : topics.slice(0, Math.max(TOPIC_PREVIEW, activeIndex + 1));

  return (
    <div className="topic-chips" role="group" aria-label={t('problems.topics')}>
      {visible.map((topic) => (
        <Chip key={topic.topic} pressed={active === topic.topic} count={topic.count} onClick={() => onToggle(topic.topic)}>
          {topic.topic}
        </Chip>
      ))}
      {topics.length > TOPIC_PREVIEW && (
        <button type="button" className="text-link topic-chips__toggle" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>
          {expanded ? t('problems.showFewerTopics') : t('problems.showAllTopics', { count: topics.length })}
        </button>
      )}
    </div>
  );
}

export function ProblemsPage() {
  const t = useStrings();
  const navigate = useNavigate();
  const { show } = useToast();
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => readFilters(params), [params]);
  const searchRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [topics, setTopics] = useState([]);
  const [continueProblem] = useState(() => storage.getJson(LAST_PROBLEM_KEY));
  const problemsState = useCatalog((state) => state.problems);
  const daily = useCatalog((state) => state.daily.data);
  const contests = useCatalog((state) => state.contests.data);
  const problems = problemsState.data;

  useDocumentTitle(t('problems.title'));

  useEffect(() => {
    const { loadProblems, loadDaily } = useCatalog.getState();
    loadProblems();
    loadDaily();
    let cancelled = false;
    practiceApi.topics().then((response) => {
      if (!cancelled && response.success) setTopics(response.data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const updateFilters = useCallback(
    (patch) => {
      setParams(writeFilters({ ...filters, ...patch }), { replace: true });
      setActiveIndex(-1);
    },
    [filters, setParams]
  );

  const clearFilters = () => {
    setParams(new URLSearchParams(), { replace: true });
    setActiveIndex(-1);
  };

  const visible = useMemo(() => applyFilters(problems ?? [], filters), [problems, filters]);
  const hasTags = (problems ?? []).some((problem) => problem.tags.length > 0);
  const contest = featuredContest(contests);
  const contestIsPrimary = Boolean(contest && contest.state === 'live' && contest.registered);
  const todayProblem = daily?.today ? (problems ?? []).find((problem) => problem.id === daily.today.questionId) : null;
  const suggestion = useMemo(() => {
    if (daily?.today || !problems) return null;
    const open = problems.filter((problem) => problem.status !== 'solved');
    const rank = { Easy: 0, Medium: 1, Hard: 2 };
    return (
      open.find((problem) => problem.id === continueProblem?.id) ??
      [...open].sort(
        (a, b) =>
          (a.status === 'attempted' ? -1 : 0) - (b.status === 'attempted' ? -1 : 0) ||
          (rank[a.difficulty] ?? 3) - (rank[b.difficulty] ?? 3) ||
          a.id - b.id
      )[0] ??
      null
    );
  }, [daily, problems, continueProblem]);
  const showContinue =
    continueProblem &&
    continueProblem.id !== daily?.today?.questionId &&
    continueProblem.id !== suggestion?.id &&
    (problems ?? []).some((problem) => problem.id === continueProblem.id && problem.status !== 'solved');

  useEffect(() => {
    const onKeyDown = (event) => {
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName) || event.target.isContentEditable;
      if (event.key === '/' && !typing) {
        event.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (typing || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.target instanceof Element && event.target.closest('[role="dialog"], [role="menu"], [role="listbox"]')) return;
      const listMode = activeIndex >= 0;
      if (event.key === 'j' || (listMode && event.key === 'ArrowDown')) {
        event.preventDefault();
        setActiveIndex((index) => Math.min(visible.length - 1, index + 1));
      } else if (event.key === 'k' || (listMode && event.key === 'ArrowUp')) {
        event.preventDefault();
        setActiveIndex((index) => Math.max(0, index - 1));
      } else if (event.key === 'Enter' && activeIndex >= 0 && visible[activeIndex] && event.target === document.body) {
        navigate(`/problems/${visible[activeIndex].id}`, { viewTransition: true });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [visible, activeIndex, navigate]);

  const pickRandom = () => {
    const pool = (visible.length > 0 ? visible : (problems ?? [])).filter((problem) => problem.status !== 'solved');
    if (pool.length === 0) {
      show(t('problems.pickRandomNone'), 'error');
      return;
    }
    navigate(`/problems/${pool[Math.floor(Math.random() * pool.length)].id}`, { viewTransition: true });
  };

  return (
    <div className="page problems-page">
      <h1 className="visually-hidden">{t('problems.title')}</h1>

      <div className="problems-hero">
        <TodayHero
          daily={daily}
          problem={todayProblem}
          suggestion={suggestion}
          primary={!contestIsPrimary}
          continueProblem={showContinue ? continueProblem : null}
        />
        <ProgressPanel problems={problems} active={filters.difficulty} onSelect={(difficulty) => updateFilters({ difficulty })} />
      </div>

      <div className="page-grid problems-body">
        <section className="problems-main" aria-labelledby="problem-list-title">
          <header className="problems-main__header">
            <h2 id="problem-list-title" className="section-title">
              {t('problems.listTitle')}
            </h2>
            {problemsState.status === 'ready' && problems?.length > 0 && (
              <span className="problems-main__count muted">{t('problems.count', { count: visible.length })}</span>
            )}
            <span className="problems-main__hint subtle">
              <Kbd>J</Kbd>
              <Kbd>K</Kbd>
              {t('problems.keyboardHint')}
            </span>
          </header>

          <div className="problem-toolbar">
            <SearchInput
              ref={searchRef}
              className="problem-toolbar__search"
              label={t('problems.searchLabel')}
              placeholder={t('problems.searchPlaceholder')}
              value={filters.query}
              onChange={(event) => updateFilters({ query: event.target.value })}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  if (filters.query) updateFilters({ query: '' });
                  else event.currentTarget.blur();
                }
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  event.currentTarget.blur();
                  setActiveIndex(0);
                }
              }}
              suffix={!filters.query && <Kbd>/</Kbd>}
            />
            <SegmentedControl
              label={t('problems.difficultyFilter')}
              value={filters.difficulty}
              onChange={(difficulty) => updateFilters({ difficulty })}
              options={[
                { value: 'all', label: t('problems.any') },
                ...DIFFICULTIES.map((level) => ({ value: level, label: t(`difficulty.${level}`) })),
              ]}
            />
            <Select
              label={t('problems.statusFilter')}
              value={filters.status}
              onChange={(status) => updateFilters({ status })}
              options={[
                { value: 'all', label: t('problems.statusAny') },
                { value: 'todo', label: t('problems.statusTodo') },
                { value: 'attempted', label: t('problems.statusAttempted') },
                { value: 'solved', label: t('problems.statusSolved') },
              ]}
            />
            <Button
              variant="ghost"
              icon={LuShuffle}
              onClick={pickRandom}
              disabled={problemsState.status !== 'ready'}
              className="problem-toolbar__random"
            >
              {t('problems.pickRandom')}
            </Button>
          </div>

          {hasTags && topics.length > 0 && (
            <TopicChips topics={topics} active={filters.topic} onToggle={(topic) => updateFilters({ topic: filters.topic === topic ? '' : topic })} />
          )}

          <div className="table-card problem-list">
            {(problemsState.status === 'loading' || problemsState.status === 'idle') && <TableSkeleton />}
            {problemsState.status === 'error' && (
              <div className="problem-list__state">
                <Alert
                  tone="danger"
                  action={
                    <Button size="sm" onClick={() => useCatalog.getState().loadProblems()}>
                      {t('common.retry')}
                    </Button>
                  }
                >
                  {t('problems.loadError')} {problemsState.error}
                </Alert>
              </div>
            )}
            {problemsState.status === 'ready' && problems.length === 0 && (
              <EmptyState icon={LuListX} title={t('problems.noProblemsTitle')} text={t('problems.noProblemsText')} />
            )}
            {problemsState.status === 'ready' && problems.length > 0 && visible.length === 0 && (
              <EmptyState
                icon={LuListX}
                title={t('problems.emptyTitle')}
                text={t('problems.emptyText')}
                action={<Button onClick={clearFilters}>{t('problems.clearFilters')}</Button>}
              />
            )}
            {problemsState.status === 'ready' && visible.length > 0 && (
              <ProblemTable
                problems={visible}
                filters={filters}
                activeIndex={activeIndex}
                onSort={(column) =>
                  updateFilters({ sort: column, direction: filters.sort === column && filters.direction === 'asc' ? 'desc' : 'asc' })
                }
              />
            )}
          </div>
          {problemsState.status === 'ready' && hasActiveFilters(filters) && visible.length > 0 && (
            <button type="button" className="text-link problem-list__clear" onClick={clearFilters}>
              {t('problems.clearFilters')}
            </button>
          )}
        </section>

        <aside className="page-rail" aria-label={t('problems.railLabel')}>
          <ContestPanel contest={contest} primary={contestIsPrimary} />
          <StreakCalendar daily={daily} />
        </aside>
      </div>
    </div>
  );
}
